/**
 * What the next outgoing call is able to carry, read from the server.
 *
 * The dial pad used to work these four out for itself out of the whole
 * telephony settings object. That was fine while one permission covered the
 * module and stopped being fine the moment it did not: after the §3.8 split an
 * operator who may only telephone people cannot read `/calls/settings` at all,
 * and a footer built on it would have drawn nothing for exactly the role the
 * split was written for.
 *
 * So the derivation moved to the server (`crm-calls/lib/dial-caps.ts`) and this
 * is the fetch. The shape is `DialCapabilities` from `crm/telephony/dialFlags`,
 * which is where the rules that USE these four live - importing the type from
 * there rather than declaring a second copy is what stops the two drifting.
 *
 * Deliberately not cached the way `callWindowPrefs` is. Those are two booleans
 * that change about once a year; these follow the settings page, and an operator
 * whose colleague has just switched recording on has to see it on their next
 * call rather than after closing the tab.
 */
import { api } from "./api";
import type { DialCapabilities } from "../crm/telephony/dialFlags";

/**
 * What to assume when the server cannot be reached.
 *
 * Everything off, which is the same thing an omitted flag means to the server:
 * the call still goes out and it goes out on the ACCOUNT's defaults. A fallback
 * that guessed `true` would draw pills promising a recording and a transcript
 * that nothing had agreed to make.
 */
export const NO_DIAL_CAPS: DialCapabilities = {
  record: false,
  transcribeAvailable: false,
  noticeAvailable: false,
  pilotNeedsNotice: false,
  // No language choice offered while the server is unreachable, for the same
  // reason as the four falses above: the call goes out on the account's own
  // wording, and a picker drawn from a guess would offer languages that may not
  // exist.
  noticeLocales: [],
  // The mobile control is not drawn while the server is unreachable, and that
  // is the safe direction for the same reason as the four falses above: the
  // fallback places an ordinary browser call, which is what the pad has always
  // done, rather than ringing a number it could not confirm is still saved.
  conference: false,
  mobileNumber: "",
};

export function getDialCapabilities(): Promise<DialCapabilities> {
  return api.get<DialCapabilities>("/admin/crm/calls/dial-caps");
}
